import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image, Video, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { uploadMedia, deleteMedia } from '../../../api/socialMedia';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_TYPES = {
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    video: ['video/mp4', 'video/quicktime', 'video/webm'],
};

const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return Image;
    if (type?.startsWith('video/')) return Video;
    return FileText;
};

const MediaUploader = ({
    mediaUrls = [],
    onMediaChange,
    maxFiles = 4,
    disabled = false
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const validateFile = (file) => {
        const allAccepted = [...ACCEPTED_TYPES.image, ...ACCEPTED_TYPES.video];

        if (!allAccepted.includes(file.type)) {
            return `Invalid file type: ${file.type}. Accepted: images (JPEG, PNG, GIF, WebP) and videos (MP4, MOV, WebM)`;
        }

        if (file.size > MAX_FILE_SIZE) {
            return `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum: 50MB`;
        }

        return null;
    };

    const handleFiles = async (files) => {
        if (disabled || uploading) return;

        const fileArray = Array.from(files);
        const remainingSlots = maxFiles - mediaUrls.length;

        if (fileArray.length > remainingSlots) {
            setError(`Can only add ${remainingSlots} more file${remainingSlots !== 1 ? 's' : ''}`);
            return;
        }

        setError(null);
        setUploading(true);
        setUploadProgress(0);

        const newUrls = [];

        for (let i = 0; i < fileArray.length; i++) {
            const file = fileArray[i];
            const validationError = validateFile(file);

            if (validationError) {
                setError(validationError);
                continue;
            }

            try {
                setUploadProgress(((i + 0.5) / fileArray.length) * 100);
                const result = await uploadMedia(file);
                // Handle various response formats
                const mediaUrl = result.url || result.data?.url || result.imageUrl || result.fileUrl;
                newUrls.push({
                    url: mediaUrl,
                    type: file.type,
                    name: file.name,
                });
                setUploadProgress(((i + 1) / fileArray.length) * 100);
            } catch (err) {
                console.error('Upload failed:', err);
                setError(err.message || `Failed to upload ${file.name}`);
            }
        }

        if (newUrls.length > 0) {
            onMediaChange([...mediaUrls, ...newUrls]);
        }

        setUploading(false);
        setUploadProgress(0);
    };

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    }, [mediaUrls]);

    const handleRemove = async (index) => {
        const mediaToRemove = mediaUrls[index];

        try {
            // Attempt to delete from server
            await deleteMedia(mediaToRemove.url);
        } catch (err) {
            console.warn('Failed to delete media from server:', err);
            // Continue with local removal even if server delete fails
        }

        const newUrls = mediaUrls.filter((_, i) => i !== index);
        onMediaChange(newUrls);
    };

    const canAddMore = mediaUrls.length < maxFiles && !disabled;

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Media <span className="text-slate-500 dark:text-slate-400 font-normal">(optional)</span>
            </label>

            {/* Error display */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2"
                    >
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            ×
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Media preview grid */}
            {mediaUrls.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                    {mediaUrls.map((media, index) => {
                        const Icon = getFileIcon(media.type);
                        const isImage = media.type?.startsWith('image/');
                        const isVideo = media.type?.startsWith('video/');

                        return (
                            <motion.div
                                key={media.url}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className="relative group aspect-video rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                            >
                                {isImage ? (
                                    <img
                                        src={media.url}
                                        alt={media.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : isVideo ? (
                                    <video
                                        src={media.url}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Icon className="w-8 h-8 text-slate-400" />
                                    </div>
                                )}

                                {/* Remove button */}
                                <button
                                    onClick={() => handleRemove(index)}
                                    className="absolute top-2 right-2 p-1.5 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>

                                {/* File type badge */}
                                <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-slate-900/70 text-white text-xs">
                                    {isImage ? 'Image' : isVideo ? 'Video' : media.name}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Upload area */}
            {canAddMore && (
                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                        dragActive
                            ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-slate-50 dark:bg-slate-800/30'
                    } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={[...ACCEPTED_TYPES.image, ...ACCEPTED_TYPES.video].join(',')}
                        onChange={(e) => handleFiles(e.target.files)}
                        className="hidden"
                    />

                    {uploading ? (
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Uploading... {Math.round(uploadProgress)}%
                            </p>
                        </div>
                    ) : (
                        <>
                            <Upload className={`w-8 h-8 mx-auto mb-2 ${
                                dragActive ? 'text-sky-500' : 'text-slate-400'
                            }`} />
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {dragActive ? 'Drop files here' : 'Click or drag files to upload'}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                Images (JPEG, PNG, GIF) or Videos (MP4, MOV) • Max 50MB
                            </p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                {mediaUrls.length} / {maxFiles} files
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MediaUploader;
